import Vue from 'vue';
import { table } from './table';
import { invite } from './invite';
import { addIntegrationPartner } from './add';
import vendorDashboardAPI from '@/utils/api/vendorDashboard';
import { genericError, genericSuccess } from '@/utils/alerts.service';
import { fileUpload } from '@/store/vendorDashboard/modules/fileUpload';
import router from '@/router/dashboard.js';
import { charCountForHtmlContent } from '@/utils/Inputs/htmlManipulationFunctions';

export const integrationPartners = {
  namespaced: true,
  state: {
    profile: {},
    useCases: [],
    useCasesToDelete: [],
    quotes: [],
    quotesToDelete: [],
    resources: [],
    newResources: [],
    resourcesToDelete: [],
    resourceLinkError: false,
    support: {},
    button: {},
    exisiting: {
      quotes: true,
      resources: true,
      support: true,
      button: true,
    },
    errors: {},
    dirty: [],
    media: [],
    mediaToDelete: [],
    videos: [],
    videosToDelete: [],
  },
  getters: {
    profileData(state) {
      return state.profile;
    },

    showHeaderBar(state) {
      return state.dirty.length > 0;
    },

    quotes(state) {
      return state.quotes.filter(q => q.quoteText);
    },

    resources(state) {
      return state.resources;
    },

    newResources(state) {
      return state.newResources;
    },

    allResources(state) {
      const activeOld = state.resources.filter(x => !x.delete);
      const activeNew = state.newResources.filter(x => !x.delete);
      const resources = [...activeOld, ...activeNew];
      resources.sort((a, b) => a.order - b.order);
      return resources;
    },

    support(state) {
      return state.support;
    },

    button(state) {
      return state.button;
    },

    categories(state, getters, rootState, rootGetters) {
      return rootGetters.categories;
    },

    collections(state, getters, rootState, rootGetters) {
      return rootGetters.collections;
    },

    media(state) {
      return state.media.filter(item => !item.delete);
    },

    useCases(state) {
      return state.useCases.filter(item => !item.delete);
    },

    getVideos(state) {
      return state.videos.filter(item => !item.delete);
    },

    partnerIsReady(state, getters) {
      return Object.keys(getters.profileData).length;
    },

    isProfilePublic(state, getters) {
      if (!getters.partnerIsReady) return undefined;
      return !getters.profileData.status.includes('H', 'N');
    },

    profileUrl(state, getters, rootState, rootGetters) {
      if (!getters.partnerIsReady) return undefined;
      return getters.isProfilePublic
        ? rootGetters.integrationsDirectoryPartnerUrlBuilder({
            slug: getters.profileData.vendorSlug,
          })
        : rootGetters.integrationsDirectoryPartnerPreviewUrlBuilder({
            id: getters.profileData.id,
            slug: getters.profileData.vendorSlug,
          });
    },
  },
  mutations: {
    setError(state, data) {
      state.errors[data.key] = {
        label: data.label,
        state: data.state,
        message: data.message,
      };
    },

    clearDirty(state) {
      state.dirty = [];
    },

    clearErrors(state) {
      state.errors = {};
    },

    setProfile(state, data) {
      state.profile = data;
    },

    setQuotes(state, data) {
      state.quotes = data;
    },

    setResources(state, data) {
      state.resources = data;
    },

    setSupport(state, data) {
      if (!data.developBy) {
        Vue.set(state.exisiting, 'support', false);
      }
      Vue.set(state, 'support', data);
    },

    setButton(state, data) {
      if (!data.id) {
        state.exisiting.button = false;
      }

      state.button = data;
    },

    setStatus(state, value) {
      state.profile.status = value;
    },

    setFeatured(state, value) {
      state.profile.featured = value;
    },

    setHeadline(state, value) {
      state.profile.headline = value;
    },

    setAboutUs(state, value) {
      state.profile.renderAboutUs = value;
    },

    updateResources(state, payload) {
      state.resources = payload;
    },

    setresourceLinkError(state, data) {
      state.resourceLinkError = data;
    },

    setSharedAboutUs(state, value) {
      state.profile.sharedAboutUs = value;
    },

    setWebsite(state, value) {
      state.profile.renderWebsite = value;
    },

    setMedia(state, value) {
      state.media = value;
    },

    setVideos(state, value) {
      state.videos = value;
    },

    addItemForMedia(state, { data }) {
      state.media.push(data);
    },

    editItemForMedia(state, { data }) {
      state.media[data.order || 0] = data;

      if (data.order >= 0 && data.id) {
        state.media[data.order || 0]['update'] = true;
      }
      state.media = [...state.media];
    },

    deleteItemForMedia(state, { item }) {
      state.media[item.order]['delete'] = true;
      state.mediaToDelete.push(state.media[item.order]);
      state.media = state.media
        .filter(i => !i.delete)
        .map((item, i) => ({
          ...item,
          order: i,
        }));
    },

    addItemForResources(state, { data }) {
      state.resources.push(data);
    },

    editItemForResources(state, { data }) {
      Vue.set(state.resources, data.order || 0, data);

      if (data.order >= 0 && data.id) {
        Vue.set(state.resources[data.order || 0], 'update', true);
      }
    },

    deleteItemForResources(state, { item }) {
      Vue.set(state.resources[item.order || 0], 'delete', true);
      state.resourcesToDelete.push(state.resources[item.order]);
      state.resources = state.resources.filter(i => !i.delete);
      state.resources = state.resources.map((item, order) => {
        return {
          ...item,
          order,
        };
      });
    },

    addItemForTestimonial(state, { data }) {
      if (state.quotes.length) {
        data.id = state.quotes[0].id;
        data.update = true;
        state.quotes = state.quotes.filter(q => q.quoteText);
      }
      state.quotes.push(data);
    },

    editItemForTestimonial(state, { data }) {
      if (state.quotes.length > 1 && !data.id) {
        data.id = state.quotes[0].id;
        state.quotes = state.quotes.filter(q => q.quoteText);
      }

      Vue.set(state.quotes, 0, data);

      if (data.id) {
        Vue.set(state.quotes[0], 'update', true);
      }
    },

    deleteItemForTestimonial(state) {
      let testimonialItems = state.quotes.filter(i => i.quoteText);
      Vue.set(testimonialItems[0], 'delete', true);
      state.quotesToDelete.push(testimonialItems[0]);
      state.quotes = state.quotes.filter(i => !i.quoteText && !i.delete);
    },

    addVideo(state, { data }) {
      state.videos.push(data);
    },

    editVideo(state, { data }) {
      state.videos[data.order || 0] = data;

      if (data.order >= 0 && data.id) {
        state.videos[data.order || 0]['update'] = true;
      }
      state.videos = [...state.videos];
    },

    deleteVideo(state, { item }) {
      let videoItems = state.videos.filter(i => i.url);
      videoItems[item.order]['delete'] = true;
      state.videosToDelete.push(videoItems[item.order]);
      (state.videos = state.videos.filter(i => !i.url)),
        videoItems
          .filter(i => !i.delete)
          .map((item, i) => ({
            ...item,
            order: i,
          }));
    },

    setUseCases(state, value) {
      state.useCases = value;
    },
    addItemForUseCases(state, { data }) {
      state.useCases.push(data);
    },

    editItemForUseCases(state, { data }) {
      Vue.set(state.useCases, data.order || 0, data);

      if (data.order >= 0 && data.id) {
        Vue.set(state.useCases[data.order || 0], 'update', true);
      }
    },

    deleteItemForUseCases(state, { item }) {
      Vue.set(state.useCases[item.order], 'delete', true);
      state.useCasesToDelete.push(state.useCases[item.order]);
      state.useCases = state.useCases
        .filter(i => !i.delete)
        .map((item, i) => ({
          ...item,
          order: i,
          update: !item.new,
        }));
    },

    updateQuote(state, payload) {
      const { key, value } = payload;
      state.quotes[key] = value;
    },

    incrementResources(state, order) {
      state.newResources.push({ order, new: true });
    },

    clearNewResources(state) {
      state.newResources = [];
    },

    updateResource(state, payload) {
      const { index, key, value } = payload;
      for (let x of state.resources) {
        if (x.order === index) {
          x[key] = value;
          x['update'] = true;
        }
      }
    },

    updateSupport(state, payload) {
      const { key, value } = payload;
      Vue.set(state.support, key, value);
    },

    updateButton(state, payload) {
      const { key, value } = payload;
      Vue.set(state.button, key, value);
    },

    addDirtyTab(state, payload) {
      return state.dirty.push(payload);
    },

    clearDirtyTabs(state) {
      state.dirty = [];
    },
    setImage(state, payload) {
      state.profile[payload.field] = payload.uri;
      state.dirty.push('profile');
    },

    addCategory(state, id) {
      state.profile.categories.push(id);
    },

    removeCategory(state, index) {
      state.profile.categories.splice(index, 1);
    },

    addCollection(state, id) {
      state.profile.collections.push(id);
    },

    removeCollection(state, index) {
      state.profile.collections.splice(index, 1);
    },
  },
  actions: {
    ...fileUpload.actions,

    async fetchProfile({ dispatch, commit }, id) {
      return vendorDashboardAPI
        .getIntegrationProfile(id)
        .then(data => {
          commit('setProfile', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    clearPartner({ commit }) {
      commit('setProfile', {});
    },

    async fetchMediaContent({ dispatch, commit }, id) {
      return vendorDashboardAPI
        .getIntegrationMedia(id)
        .then(data => {
          const media = data.filter(i => !i.url);
          const videos = data.filter(i => i.url);
          media.sort((a, b) => {
            if (a.order < b.order) return -1;
            if (a.order > b.order) return 1;
            return 0;
          });
          videos.sort((a, b) => {
            if (a.order < b.order) return -1;
            if (a.order > b.order) return 1;
            return 0;
          });
          commit('setMedia', media);
          commit('setVideos', videos);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchUseCases({ dispatch, commit }, id) {
      commit('setUseCases', []);
      return vendorDashboardAPI
        .getIntegrationUseCases(id)
        .then(data => {
          let { results } = data;
          results
            .sort((a, b) => {
              if (a.order < b.order) return -1;
              if (a.order > b.order) return 1;
              return 0;
            })
            .forEach((result, i) => {
              result.order = i;
            });
          commit('setUseCases', results);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchQuotes({ dispatch, commit }, id) {
      return vendorDashboardAPI
        .getIntegrationQuotes(id)
        .then(data => {
          const hasQuote = data[0].id;
          commit('setQuotes', !hasQuote ? [] : data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchResources({ dispatch, commit }, id) {
      commit('setResources', []);
      commit('clearNewResources');
      return vendorDashboardAPI
        .getIntegrationResources(id)
        .then(data => {
          commit('setResources', data.results);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchSupport({ dispatch, commit }, id) {
      return vendorDashboardAPI
        .getIntegrationSupport(id)
        .then(data => {
          commit('setSupport', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async fetchButton({ dispatch, commit }, id) {
      return vendorDashboardAPI
        .getIntegrationCTA(id)
        .then(data => {
          commit('setButton', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async removePartner({ dispatch }, id) {
      return vendorDashboardAPI
        .deleteIntegrationPartner(id)
        .then(() => {
          dispatch('showUserMessage', { ...genericSuccess }, { root: true });
          router.push({ name: 'IntegrationPartners' });
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    updateStatus({ commit }, value) {
      commit('setStatus', value);
    },

    updateFeatured({ commit }, value) {
      commit('setFeatured', value);
    },

    updateHeadline({ commit }, value) {
      commit('setHeadline', value);
    },

    updateAboutUs({ commit }, value) {
      commit('setAboutUs', value);
    },

    updateWebsite({ commit }, value) {
      commit('setWebsite', value);
    },

    updateSharedAboutUs({ commit }, value) {
      commit('setSharedAboutUs', value);
    },

    addItemForUseCases({ commit, dispatch }, payload) {
      commit('addItemForUseCases', payload);
      dispatch('addDirtyTab', { storeKey: 'useCases' });
    },

    editItemForUseCases({ commit, dispatch }, payload) {
      commit('editItemForUseCases', payload);
      dispatch('addDirtyTab', { storeKey: 'useCases' });
    },

    deleteItemForUseCases({ commit, dispatch }, payload) {
      commit('deleteItemForUseCases', payload);
      dispatch('addDirtyTab', { storeKey: 'useCases' });
    },

    removeNewUseCase({ commit }, index) {
      commit('deleteNewUseCase', index);
    },

    updateQuote({ commit }, payload) {
      commit('updateQuote', payload);
    },

    addResource({ commit }, position) {
      commit('incrementResources', position);
    },

    updateResource({ commit }, payload) {
      commit('updateResource', payload);
    },

    addItemForMedia({ commit, dispatch }, payload) {
      commit('addItemForMedia', payload);
      dispatch('addDirtyTab', { storeKey: 'media' });
    },

    editItemForMedia({ commit, dispatch }, payload) {
      commit('editItemForMedia', payload);
      dispatch('addDirtyTab', { storeKey: 'media' });
    },

    deleteItemForMedia({ commit, dispatch }, payload) {
      commit('deleteItemForMedia', payload);
      dispatch('addDirtyTab', { storeKey: 'media' });
    },

    addItemForResources({ commit, dispatch }, payload) {
      commit('addItemForResources', payload);
      dispatch('addDirtyTab', { storeKey: 'resources' });
    },

    editItemForResources({ commit, dispatch }, payload) {
      commit('editItemForResources', payload);
      dispatch('addDirtyTab', { storeKey: 'resources' });
    },

    deleteItemForResources({ commit, dispatch }, payload) {
      commit('deleteItemForResources', payload);
      dispatch('addDirtyTab', { storeKey: 'resources' });
    },

    addItemForTestimonial({ commit, dispatch }, payload) {
      commit('addItemForTestimonial', payload);
      dispatch('addDirtyTab', { storeKey: 'quotes' });
    },

    editItemForTestimonial({ commit, dispatch }, payload) {
      commit('editItemForTestimonial', payload);
      dispatch('addDirtyTab', { storeKey: 'quotes' });
    },

    deleteItemForTestimonial({ commit, dispatch }, payload) {
      commit('deleteItemForTestimonial', payload);
      dispatch('addDirtyTab', { storeKey: 'quotes' });
    },

    addVideo({ commit, dispatch }, payload) {
      commit('addVideo', payload);
      dispatch('addDirtyTab', { storeKey: 'media' });
    },

    editVideo({ commit, dispatch }, payload) {
      commit('editVideo', payload);
      dispatch('addDirtyTab', { storeKey: 'media' });
    },

    deleteVideo({ commit, dispatch }, payload) {
      commit('deleteVideo', payload);
      dispatch('addDirtyTab', { storeKey: 'media' });
    },

    updateError({ commit }, payload) {
      if (payload.key === 'resourceLink') {
        commit('setresourceLinkError', payload.state);
      } else {
        commit('setError', payload);
      }
    },

    handleCheckboxChange({ commit, state }, { id, type }) {
      let add = true;
      let index = 0;

      const types = {
        categories: 'Category',
        collections: 'Collection',
      };

      const data = state.profile[type];
      data.forEach((type, i) => {
        if (type === id) {
          add = false;
          index = i;
        }
      });

      if (add) {
        commit(`add${types[type]}`, id);
        return commit('addDirtyTab', 'profile');
      }
      commit(`remove${types[type]}`, index);
      return commit('addDirtyTab', 'profile');
    },

    updateSupport({ commit }, payload) {
      commit('updateSupport', payload);
    },

    updateButton({ commit }, payload) {
      commit('updateButton', payload);
    },

    addDirtyTab({ commit, state }, payload) {
      if (!state.dirty.includes(payload.storeKey)) {
        commit('addDirtyTab', payload.storeKey);
      }
    },

    checkMainFields({ state, commit }) {
      return new Promise(resolve => {
        // Error check logo
        if (!state.profile.renderImage || state.profile.renderImage === '') {
          commit('setError', {
            key: 'companyLogo',
            state: true,
            label: 'Company logo',
            message: 'Please upload a company logo',
          });
          document
            .querySelector('#companyLogo .file-input__area')
            .classList.add('fileError');
        } else {
          commit('setError', {
            key: 'companyLogo',
            state: false,
            label: 'Company logo',
          });
        }
        if (charCountForHtmlContent(state.profile.renderAboutUs) === 0) {
          commit('setError', {
            key: 'companyRenderAbout',
            state: true,
            label: 'Company description',
            message: `Please enter the company's description`,
          });
          document
            .getElementById('companyRenderAbout')
            .classList.add('contentError');
          document
            .getElementById('companyRenderAbout')
            .parentNode.querySelector('.editorButtons')
            .classList.add('buttonsError');
          document
            .getElementById('companyRenderAbout')
            .parentNode.querySelector('.countArea')
            .classList.add('countError');
        } else {
          commit('setError', {
            key: 'companyRenderAbout',
            state: false,
            label: 'Company description',
          });
        }
        // Error check website
        if (
          !state.profile.renderWebsite ||
          state.profile.renderWebsite === '' ||
          !/^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
            String(state.profile.renderWebsite).toLowerCase()
          )
        ) {
          commit('setError', {
            key: 'profileWebsite',
            state: true,
            label: 'Company website',
            message:
              'Please enter a valid website URL, containing either http:// or https://',
          });
          document.getElementById('profileWebsite').classList.add('inputError');
        } else {
          commit('setError', {
            key: 'profileWebsite',
            state: false,
            label: 'Company website',
          });
        }
        resolve();
      });
    },

    async saveChanges({ dispatch, state, commit }, id) {
      dispatch('checkMainFields').then(() => {
        if (Object.keys(state.errors).length) {
          for (let key of Object.keys(state.errors)) {
            if (state.errors[key].state) {
              dispatch(
                'showUserMessage',
                {
                  success: false,
                  title: 'Oops!',
                  message:
                    state.errors[key].message ||
                    'Please fill in the ' + state.errors[key].label + ' field.',
                },
                { root: true }
              );
              return;
            }
          }
        }

        // Use cases
        if (state.useCases && state.useCases.length) {
          for (let i of [...state.useCases, ...state.useCasesToDelete]) {
            if (!i.title || !i.description) {
              const label = !i.title ? 'title' : 'description';
              return dispatch(
                'showUserMessage',
                {
                  success: false,
                  title: 'Oops!',
                  message: `Please make sure all use cases have a ${label}`,
                },
                { root: true }
              );
            }
          }
        }

        // Call to action section
        if (state.button && state.button.text) {
          if (state.button.text.length > 25) {
            return dispatch(
              'showUserMessage',
              {
                success: false,
                title: 'Oops!',
                message: `Please ensure that the button text/label has no more than 25 characters`,
              },
              { root: true }
            );
          }
        }

        let promises = [];

        if (state.dirty.includes('profile')) {
          promises.push(saveProfile(dispatch, state, id, commit));
        }

        if (state.dirty.includes('media')) {
          promises.push(saveMedia(dispatch, state, id, commit));
        }

        if (state.dirty.includes('useCases')) {
          promises.push(saveUseCases(dispatch, state, id, commit));
        }

        if (state.dirty.includes('quotes')) {
          promises.push(saveQuote(dispatch, state, id, commit));
        }

        if (state.dirty.includes('resources')) {
          promises.push(saveResources(dispatch, state, id, commit));
        }

        if (state.dirty.includes('support')) {
          promises.push(saveSupport(dispatch, state, id, commit));
        }

        if (state.dirty.includes('button')) {
          promises.push(saveButton(dispatch, state, id, commit));
        }

        commit('resetSaveProgress', null, { root: true });
        commit('setSaveTotalItems', promises.length + 1, { root: true });
        commit('setSaving', 'saving', { root: true });
        commit('addSaveProgress', null, { root: true });

        Promise.all(promises).then(() => {
          dispatch(
            'showUserMessage',
            {
              success: true,
              title: 'Success!',
              message: 'Your changes have been made.',
            },
            { root: true }
          );

          commit('clearDirty');
          commit('setSaving', 'idle', { root: true });
        });
      });
    },
  },

  modules: {
    table,
    invite,
    addIntegrationPartner,
  },
};

async function saveProfile(dispatch, state, id, commit) {
  return new Promise((resolve, reject) => {
    const data = {
      ...state.profile,
      website: state.profile.renderWebsite,
      aboutUs: state.profile.renderAboutUs,
    };
    if (state.profile.renderImage != '') {
      data['image'] = state.profile.renderImage;
    }
    return vendorDashboardAPI
      .saveIntegrationProfile(id, data)
      .then(() => {
        commit('addSaveProgress', null, { root: true });
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
}

async function saveMedia(dispatch, state, id, commit) {
  return new Promise((resolve, reject) => {
    const media = [
      ...state.media,
      ...state.mediaToDelete,
      ...state.videos,
      ...state.videosToDelete,
    ];

    const data = media.map(i => {
      if (i.uri) {
        return {
          ...i,
          file: i.uri,
          uri: null,
        };
      }
      return { ...i };
    });

    return vendorDashboardAPI
      .saveIntegrationMedia(id, data)
      .then(() => {
        commit('addSaveProgress', null, { root: true });
        dispatch('fetchMediaContent', id);
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
}

async function saveUseCases(dispatch, state, id, commit) {
  return new Promise((resolve, reject) => {
    const data = [...state.useCases, ...state.useCasesToDelete];
    return vendorDashboardAPI
      .saveIntegrationUseCases(id, data)
      .then(res => {
        const x = res.data.map((item, i) => {
          item.order = i;
          return item;
        });
        commit('addSaveProgress', null, { root: true });
        commit('setUseCases', x);
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
}

async function saveQuote(dispatch, state, id, commit) {
  return new Promise((resolve, reject) => {
    const data = [...state.quotes, ...state.quotesToDelete]
      .filter(q => q.quoteText)
      .map(item => ({ ...item, techPartner: id }));
    return vendorDashboardAPI
      .saveIntegrationQuote(id, data)
      .then(res => {
        const hasQuote = res.data[0].id;
        commit('setQuotes', !hasQuote ? [] : res.data);
        commit('addSaveProgress', null, { root: true });
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
}

async function saveResources(dispatch, state, id, commit) {
  return new Promise((resolve, reject) => {
    const resources = [...state.resources, ...state.resourcesToDelete];

    const data = resources.map(i => {
      const item = { ...i };
      if (i.file === '') {
        item.file = undefined;
      }
      if (i.uri) {
        item.file = i.uri;
        item.uri = null;
      }
      return item;
    });

    return vendorDashboardAPI
      .saveIntegrationResources(id, data)
      .then(res => {
        const x = res.data.map((item, i) => {
          item.order = i;
          return item;
        });
        commit('addSaveProgress', null, { root: true });
        commit('clearNewResources');
        commit('setResources', x);
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
}

async function saveSupport(dispatch, state, id, commit) {
  return new Promise((resolve, reject) => {
    if (state.exisiting.support) {
      return vendorDashboardAPI
        .updateIntegrationSupport(id, state.support)
        .then(() => {
          commit('addSaveProgress', null, { root: true });
          resolve();
        })
        .catch(() => {
          reject();
        });
    } else {
      return vendorDashboardAPI
        .saveIntegrationSupport(id, state.support)
        .then(() => {
          commit('addSaveProgress', null, { root: true });
          resolve();
        })
        .catch(() => {
          reject();
        });
    }
  });
}

async function saveButton(dispatch, state, id, commit) {
  return new Promise((resolve, reject) => {
    if (state.exisiting.button) {
      return vendorDashboardAPI
        .updateIntegrationCTA(id, state.button)
        .then(() => {
          commit('addSaveProgress', null, { root: true });
          dispatch('fetchButton', id);
          resolve();
        })
        .catch(() => {
          reject();
        });
    } else {
      return vendorDashboardAPI
        .saveIntegrationCTA(id, state.button)
        .then(() => {
          commit('addSaveProgress', null, { root: true });
          dispatch('fetchButton', id);
          resolve();
        })
        .catch(() => {
          reject();
        });
    }
  });
}
